/**
 * CR500 Phase 5A: BackNavBlock
 * CR324: Gets onFlipBack from context (not config)
 * 
 * Renders the "Back" button for flipping back to the front face.
 * This block is rendered in the details surface footer.
 * 
 * The flip handler is passed via context.onFlipBack from FlipPresentation.
 */

import React from "react";
import { RotateCcw } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";

export const BackNavBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // Get flip handler from context (passed by FlipPresentation via DetailsBlocksRenderer)
  const onFlipBack = context.onFlipBack;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFlipBack) {
      onFlipBack();
    }
  };

  return (
    <div 
      className="flex-shrink-0 border-t bg-muted/20 p-2 flex justify-end"
      data-block="back_nav"
      data-block-id={block.id}
    >
      <button
        type="button"
        onClick={handleClick}
        className="min-w-[44px] min-h-[44px] h-11 px-3 flex items-center justify-center gap-1.5 bg-background hover:bg-muted shadow-sm border border-border rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Flip back to front"
        title="Back to front"
      >
        <RotateCcw className="h-4 w-4" />
        <span className="text-xs font-medium">Back</span>
      </button>
    </div>
  );
};

export default BackNavBlock;
